import { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Checkbox, FormControlLabel, FormGroup,
  Button, Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  getInspectorConfig, updateInspectorRegions, updateInspectorResourceTypes,
  disableInspector, startInspectorRolesScan, getInspectorRolesJob, fixInspectorRoles,
} from '../../../services/inspectorApi';
import RolePermissionsPanel from '../../../components/portal/securityhub/RolePermissionsPanel';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import MultiSelectField from './MultiSelectField';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { INSPECTOR_SCAN_TYPES, SCAN_TYPE_PRICE_DISCLAIMER } from './inspectorScanTypes';
import {
  CK, FONT_BODY, listCardSx, sectionCardSx, sectionHeadingSx, dialogProps, dialogTitleSx,
} from '../portalStyles';

const INSPECTOR_COLOR = '#7D3C98';
const INSPECTOR_COLOR_DARK = '#652F7D';

const inspectorButtonSx = {
  backgroundColor: INSPECTOR_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: INSPECTOR_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };
const hintTextSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };

const settingsHeader = (
  <PortalPageHeader
    icon={<SettingsIcon sx={{ fontSize: 24 }} />}
    title="Settings"
    subtitle="Manage scan types, linking regions, account permissions and Inspector status"
    color={INSPECTOR_COLOR}
  />
);

// The scan-type list and its copy now live in inspectorScanTypes.js, shared with
// InspectorSetup — see that file for why it is a wire contract and why it is not
// derived from the engine descriptor's shorter `capability.items`.

// InspectorConfigResponse decodes both of these to []string, but tolerate a raw
// JSON string so a DTO change cannot blank the form.
const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

const sameSet = (a, b) => a.length === b.length
  && [...a].sort().join('|') === [...b].sort().join('|');

// Region and scan-type updates claim the config atomically and are refused unless
// it is settled, so a Save dispatched during another operation is a guaranteed
// "another operation is in progress" error.
const configAllowsUpdate = (status) => status === 'enabled' || status === 'partial_failure';

export default function InspectorSettingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [types, setTypes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [confirm, setConfirm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regionConfirmOpen, setRegionConfirmOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Extracted from the mount effect so the error state below can offer a real
  // retry. Seeding the two form fields from the response is safe here and only
  // here: this runs when there is no config yet, so there is no edit to lose.
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const c = await getInspectorConfig();
      const cfg = c?.data || null;
      setConfig(cfg);
      if (cfg) {
        setTypes(toStringArray(cfg.resourceTypes));
        setRegions(toStringArray(cfg.linkingRegions));
      }
      setErr(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const operationRunning = config?.status === 'in_progress';

  // Every Save on this page is gated on a settled config, so without a poll a
  // visitor who arrives mid-operation would sit in front of permanently disabled
  // buttons until they reloaded by hand. Only the config is refreshed — never the
  // two form fields, which would otherwise overwrite an edit in progress.
  useEffect(() => {
    if (!operationRunning) return undefined;
    const id = setInterval(async () => {
      try {
        const c = await getInspectorConfig();
        setConfig(c?.data || null);
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [operationRunning]);

  const save = async (fn, destination) => {
    setBusy(true);
    try {
      await fn();
      setErr(null);
      navigate(destination);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveTypes = () => {
    // Belt to the Save button's braces: the backend rejects an empty list with
    // "at least one scan type is required", which would surface as a bare error
    // banner after the user thought they had turned Inspector's scanning off.
    if (types.length === 0) {
      setErr('Select at least one scan type — Amazon Inspector cannot run with none.');
      return;
    }
    // Hand off to Enrollment: a scan-type change fans out over every enrolled
    // account, and that page is where a per-account failure becomes actionable.
    save(() => updateInspectorResourceTypes(types), '/securityhub/inspector/enrollment');
  };

  const saveRegions = () => {
    save(() => updateInspectorRegions(regions), '/securityhub/inspector/enrollment');
  };

  const closeDisableDialog = () => {
    setConfirmOpen(false);
    setConfirm('');
  };

  // The dialog closes BEFORE the call, matching the region-confirm dialog. Left
  // until after the await, a rejection would sit behind the still-open modal on top
  // of the page-level error banner: the button re-enables, the user clicks again,
  // and nothing appears to happen.
  //
  // Destination is the Inspector entry — it polls the config and shows the teardown
  // progress, then the setup wizard once the status reaches "disabled".
  const doDisable = () => {
    closeDisableDialog();
    return save(() => disableInspector(), '/securityhub/inspector');
  };

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Inspector settings…" color={INSPECTOR_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  // MUST precede the not-configured branch. GET /config rejecting leaves `config`
  // null, and the branch below would then tell an operator whose Inspector is
  // configured and running that it was never set up — while discarding the actual
  // error, which is already sitting in `err`.
  if (err && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={err} onRetry={loadConfig} />
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/inspector')}>
              Go to setup
            </Button>
          }>
            Amazon Inspector is not set up yet for this customer. Complete the setup
            wizard first to manage scan types, regions, or disable the service.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const storedTypes = toStringArray(config.resourceTypes);
  const storedRegions = toStringArray(config.linkingRegions);
  const canUpdate = configAllowsUpdate(config.status);
  // "failed" is terminal, not transitional: no fan-out is running, and the update
  // gates refuse it until setup is re-run. Without this the Save buttons are dead
  // with no explanation whatsoever. Disable stays available — the backend accepts
  // it from every settled status — which is the other way out of this state.
  const setupFailed = config.status === 'failed';
  const typesDirty = !sameSet(types, storedTypes);
  // The home region is force-prepended server-side and locked in the picker, so it
  // can never appear here; anything that does is genuinely being torn down.
  const removedRegions = storedRegions.filter((r) => !regions.includes(r));
  const addedRegions = regions.filter((r) => !storedRegions.includes(r));
  const regionsDirty = removedRegions.length > 0 || addedRegions.length > 0;

  const toggleType = (value) => {
    setTypes((prev) => (prev.includes(value)
      ? prev.filter((t) => t !== value)
      : [...prev, value]));
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
      {err && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>
        </motion.div>
      )}

      {operationRunning && (
        <motion.div variants={sectionVariants}>
          <OperationProgress
            percent={config.progress}
            label={config.progressLabel || 'An Inspector operation is running — settings are locked until it finishes'}
            sx={{ mb: 2 }}
          />
        </motion.div>
      )}

      {setupFailed && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/inspector')}>
                Re-run setup
              </Button>
            )}
          >
            Amazon Inspector setup failed{config.errorMessage ? `: ${config.errorMessage}` : '.'}
            {' '}
            Nothing is running and nothing will resume on its own. Scan types and
            regions cannot be changed until setup is re-run — disabling Inspector
            from the Danger zone still works.
          </Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={listCardSx}>
        <Tabs
          value={tab}
          // Clear the banner on the way out: an error raised by a Save on one tab
          // describes that tab only, and left pinned it reads as a problem with
          // whichever tab the user has just moved to.
          onChange={(_, v) => { setErr(null); setTab(v); }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: `1px solid ${CK.border}`,
            '& .MuiTab-root': {
              fontFamily: FONT_BODY,
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'none',
              minHeight: 56,
              color: CK.body,
              '&.Mui-selected': { color: INSPECTOR_COLOR },
            },
            '& .MuiTabs-indicator': { backgroundColor: INSPECTOR_COLOR },
          }}
        >
          <Tab label="Scan types" />
          <Tab label="Regions" />
          <Tab label="Permissions" />
          <Tab label="Danger zone" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Scan types</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 2 }}>
              Which kinds of resource Amazon Inspector scans, in every enrolled
              account and linking region. Turning a type off stops its scanning and
              its charges; turning one on starts both.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Each scan type is billed separately by Amazon Inspector. This is where
              a customer trims the bill — enable only the resource kinds you
              actually need covered.
            </Alert>
            <FormGroup sx={{ mb: 1 }}>
              {INSPECTOR_SCAN_TYPES.map((t) => (
                <Box key={t.value} sx={{ mb: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={types.includes(t.value)}
                        onChange={() => toggleType(t.value)}
                        sx={{ '&.Mui-checked': { color: INSPECTOR_COLOR } }}
                      />
                    }
                    label={t.label}
                    sx={{ display: 'block', mb: 0.25 }}
                  />
                  <Typography sx={{ ...hintTextSx, ml: 4 }}>{t.description}</Typography>
                  <Typography sx={{ ...hintTextSx, ml: 4, fontWeight: 600 }}>{t.price}</Typography>
                </Box>
              ))}
            </FormGroup>
            {/* The prices travel with the list, so the caveat has to travel with
                them: a bare figure next to a checkbox reads as a quote. */}
            <Typography sx={{ ...hintTextSx, mb: 2 }}>{SCAN_TYPE_PRICE_DISCLAIMER}</Typography>
            {types.length === 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                At least one scan type is required. Amazon Inspector cannot be left
                with none — to stop scanning entirely, disable Inspector from the
                Danger zone.
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={busy || !canUpdate || types.length === 0 || !typesDirty}
              onClick={saveTypes}
              sx={inspectorButtonSx}
            >
              Save scan types
            </Button>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Linking regions</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              Home region: <b>{config.homeRegion}</b> (read-only)
            </Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              This list is not a display filter. Adding a region enables Inspector
              there for every enrolled account and starts billing; removing one
              disables Inspector in that region in every enrolled account and
              deletes the findings Prism has cached from it.
            </Typography>
            <Typography sx={{ ...hintTextSx, mb: 2 }}>
              Amazon Inspector is not available in every region. Selecting an
              unsupported region will cause enablement to fail for that region only —
              the rest of the update still succeeds.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <MultiSelectField
                label="Linking regions"
                options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
                value={regions}
                onChange={setRegions}
                lockedValues={config.homeRegion ? [config.homeRegion] : []}
              />
            </Box>
            {removedRegions.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Saving will tear down <b>{removedRegions.join(', ')}</b>.
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={busy || !canUpdate || !regionsDirty}
              onClick={() => (removedRegions.length > 0 ? setRegionConfirmOpen(true) : saveRegions())}
              sx={inspectorButtonSx}
            >
              Save regions
            </Button>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <RolePermissionsPanel
              capabilityLabel="Amazon Inspector"
              startScan={startInspectorRolesScan}
              getJob={getInspectorRolesJob}
              fix={fixInspectorRoles}
            />
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Disable Inspector</Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Disabling turns Inspector off in every enrolled account and region and
              removes the cached findings. On a clean teardown Prism also detaches
              the IAM policy it added to each account&apos;s access role.
            </Alert>
            <Button
              color="error"
              variant="contained"
              disabled={busy || operationRunning}
              onClick={() => setConfirmOpen(true)}
              sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
            >
              Disable Inspector
            </Button>
          </Box>
        )}
      </Paper>
      </motion.div>

      <Dialog
        open={regionConfirmOpen}
        onClose={() => setRegionConfirmOpen(false)}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Confirm region removal</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1 }}>
            Removing <b>{removedRegions.join(', ')}</b> disables Amazon Inspector in
            {removedRegions.length === 1 ? ' that region' : ' those regions'} for
            every enrolled account and deletes the findings cached from
            {removedRegions.length === 1 ? ' it' : ' them'}. Scanning stops and the
            charges stop with it. Adding the
            {removedRegions.length === 1 ? ' region' : ' regions'} back later starts
            a fresh scan.
          </Typography>
          {addedRegions.length > 0 && (
            <Typography sx={{ ...bodyTextSx, mt: 2 }}>
              The same save enables <b>{addedRegions.join(', ')}</b>.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setRegionConfirmOpen(false)}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => { setRegionConfirmOpen(false); saveRegions(); }}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Save regions
          </Button>
        </DialogActions>
      </Dialog>

      {/* The typed customer ID is cleared on every exit. Left in state, reopening
          the dialog arrives with the type-to-confirm guard already satisfied and
          Disable live on the first click — which is the whole thing the guard
          exists to prevent. */}
      <Dialog
        open={confirmOpen}
        onClose={closeDisableDialog}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Confirm disable</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1 }}>
            Type the customer ID <b>{config.customerId}</b> to confirm:
          </Typography>
          <TextField
            fullWidth
            label="Customer ID"
            placeholder={config.customerId}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeDisableDialog}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy || confirm !== config.customerId}
            onClick={doDisable}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
